// Pure helpers for the transaction split editor. Kept free of Svelte/DOM so the
// allocation and validation rules can be unit-tested directly.

import { round2 } from './format';
import { isUncategorised, categoryOrUncat } from './category';
import type { Split } from './db';

// Re-exported for existing importers (debt.ts, the debt detail page); the
// canonical definition lives in ./format.
export { round2 };

export interface SplitLine {
	category: string;
	amount: number;
	note?: string;
	// When true this line is money movement (e.g. the transfer portion of a debt
	// payment); it is skipped by budget aggregation and may be uncategorised.
	excludeFromBudget?: boolean;
}

function lineSum(lines: SplitLine[]): number {
	return lines.reduce((s, l) => s + (Number.isFinite(l.amount) ? l.amount : 0), 0);
}

// Amount still unallocated: the transaction amount minus the sum of the lines,
// rounded to cents. Zero means the lines add up exactly.
export function splitRemainder(txAmount: number, lines: SplitLine[]): number {
	return round2(txAmount - lineSum(lines));
}

export function sameSign(a: number, b: number): boolean {
	return (a > 0 && b > 0) || (a < 0 && b < 0);
}

// Returns null when the lines form a valid split of txAmount, otherwise a short
// human-readable reason for the first problem found.
export function validateSplitLines(txAmount: number, lines: SplitLine[]): string | null {
	if (lines.length < 2) return 'Add at least two split lines';
	let counted = 0;
	for (const l of lines) {
		// Excluded lines are money movement (e.g. a transfer portion) and may be
		// UNCAT; counted lines need a real category.
		if (!l.excludeFromBudget) {
			if (isUncategorised(l.category)) return 'Each line needs a category';
			counted++;
		}
		if (!Number.isFinite(l.amount) || l.amount === 0) return 'Each line needs a non-zero amount';
		if (!sameSign(l.amount, txAmount)) return 'Each amount must match the transaction sign';
	}
	if (counted === 0) return 'A split needs at least one counted line';
	if (splitRemainder(txAmount, lines) !== 0) return 'Lines must add up to the transaction amount';
	return null;
}

// Drops any rounding residue onto the last line so the lines sum exactly to
// txAmount. Returns a new array; the input is not mutated.
export function distributeRemainder(txAmount: number, lines: SplitLine[]): SplitLine[] {
	const out = lines.map((l) => ({ ...l }));
	if (out.length === 0) return out;
	const rem = splitRemainder(txAmount, out);
	if (rem === 0) return out;
	const last = out.length - 1;
	out[last] = { ...out[last], amount: round2((Number.isFinite(out[last].amount) ? out[last].amount : 0) + rem) };
	return out;
}

// True when an existing split no longer sums to the transaction amount (e.g. a
// pending amount drifted on re-ingest), so the SPA can prompt a re-split.
export function isSplitOutOfDate(txAmount: number, lines: SplitLine[] | undefined): boolean {
	if (!lines || lines.length === 0) return false;
	return Math.abs(splitRemainder(txAmount, lines)) > 0.005;
}

// --- Split-editor row model ---
// The editor keeps amounts as strings (raw keystrokes) until conversion.

export interface SplitRow {
	id: number;
	category: string;
	amount: string;
	note: string;
	excludeFromBudget: boolean;
}

// Convert the editor rows into the SplitLine shape the validation/remainder
// helpers consume. Amounts become numbers (NaN for unparseable, which the
// validators reject) and blank notes drop to undefined.
export function rowsToSplitLines(rows: SplitRow[]): SplitLine[] {
	return rows.map((r) => ({
		category: r.category,
		amount: Number(r.amount),
		note: r.note.trim() || undefined,
		excludeFromBudget: r.excludeFromBudget,
	}));
}

// Build the persisted Split[] payload from validated lines: excluded (money
// movement) lines fall back to UNCAT, and the optional note/excludeFromBudget
// fields are omitted unless set.
export function buildSplitPayload(lines: SplitLine[]): Split[] {
	return lines.map((l) => ({
		category: l.excludeFromBudget ? categoryOrUncat(l.category) : l.category,
		amount: l.amount,
		...(l.note ? { note: l.note } : {}),
		...(l.excludeFromBudget ? { excludeFromBudget: true } : {}),
	}));
}
